import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';

import ContainerLayout from '@/components/layouts/ContainerLayout';
import Breadcrumbs from '@/components/modules/breadcrumbs/Breadcrumbs';

import BookAppointment from '@/components/modules/modals/BookAppointment/BookAppointment';
import Photos from '@/components/modules/place/Photos';
import SEO from '@/components/modules/SEO';
import { APP_ROUTES } from '@/constants/pages';
import { handleShowTabItemClick } from '@/features/PlacePages/helpers/navigation';
import FakeSearchForm from '@/features/searchV2/modules/FakeSearchForm/FakeSearchForm';
import { url } from '@/helpers';
import { useAppDispatch } from '@/hooks';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { Fragment, useRef } from 'react';
import { BrandProductImage } from '../../components/BrandProductImage';
import ButtonAndPriceContainer from '../../components/elements/ButtonAndPriceContainer';
import { mapCroLabelProps } from '../../helpers';
import DealsSectionPreview, { DealsSectionPreviewSkeleton } from '../../modules/DealSectionPreview';
import { DealSectionReviews } from '../../modules/DealSectionReviews';
import { useDealReviews } from '../../modules/DealSectionReviews.hooks';
import {
  AboutSection,
  BrandProductCard,
  BusinessAddress,
  ConditionsSection,
  DealCTAContainer,
  DealHeader,
  FloatingCTAWrapper,
} from './DealDetails.components';
import { useDealDetails } from './DealDetails.hooks';
import DealNotAvailable from './DealNotAvailable';

const DealDetails = () => {
  const {
    deal,
    searchPreview,
    loading,
    errorType,
    slugId,
    searchedKeyword,
    searchedLocation,
    onBackClick,
    onBookClick,
  } = useDealDetails();
  const dispatch = useAppDispatch();
  const { isMobileView } = useMobileView();
  const ctaContainerRef = useRef<HTMLDivElement>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const onReviewsClick = () => handleShowTabItemClick(reviewsRef);
  const { reviewsData, isFetching: isFetchingReviews } = useDealReviews(
    deal?.place?.id,
    !!deal?.place?.settings?.displayReviews,
  );
  const ctaPosition = useIntersectionObserver(ctaContainerRef, { root: null, threshold: 0.3 });
  const isCTAIntersecting = ctaPosition === 'inView';
  const showStickyNav = !loading && (isMobileView || (ctaContainerRef.current !== null && !isCTAIntersecting));

  if (errorType) {
    return <DealNotAvailable errorType={errorType} sections={searchPreview} />;
  }

  const croLabelProps = mapCroLabelProps(deal?.croLabels?.[0]);
  const componentProps = loading ? { deal: null, loading: true } : { deal, loading: false };
  const photosProps = {
    photos: deal?.imageUrl ? [deal.imageUrl] : [],
    place: { ...deal?.place, about: { slug: deal?.place?.slug } },
    hideGridPreview: true,
    loading,
  };

  return (
    <PageViewLayout
      type="mainView"
      back
      middleSlot={<FakeSearchForm type="deals" />}
      onBackButtonClick={onBackClick}
      wrapperClass="bg-surfaceColors-surface_mp"
      hideBottomNavBar>
      <ContainerLayout className="py-md md:pb-huge mx-auto !max-w-[1040px]">
        <div>
          <div className="space-y-xl lg:space-y-cv-margin pb-sm mb-xl xl:mb-cv-margin xl:pb-0">
            {(searchedKeyword || searchedLocation) && (
              <Breadcrumbs
                className="py-xs"
                items={[
                  { title: 'Deals', url: '' },
                  ...(searchedLocation ? [{ title: searchedLocation, url: '' }] : []),
                  { title: searchedKeyword ?? _t('AllSweden'), url: '' },
                ]}
              />
            )}
            <DealHeader {...componentProps} onReviewsClick={onReviewsClick} />
          </div>

          <div className={`flex ${isMobileView ? 'space-y-xl flex-col' : 'gap-xl'}`}>
            {/* Left column - Main content */}
            <div className="space-y-xl md:space-y-cv-padding flex-1 md:w-2/3">
              <div className="@container relative">
                <Photos {...photosProps} hideGridPreview />
                {deal?.brand?.template.logoImageUrlSecondary && (
                  <div className="left-lg bottom-lg absolute">
                    <img src={deal.brand.template.logoImageUrlSecondary} style={{ height: 40 }} />
                  </div>
                )}
                {deal?.brand?.productImageUrl && (
                  <div className="right-lg bottom-lg absolute">
                    <BrandProductImage size="scale" imgSrc={deal.brand.productImageUrl} />
                  </div>
                )}
              </div>
              <AboutSection {...componentProps} />
              <BrandProductCard {...componentProps} />
              <ConditionsSection {...componentProps} />
              {!isMobileView && !loading && deal && (
                <DealSectionReviews
                  {...componentProps}
                  reviewsRef={reviewsRef}
                  reviewsData={reviewsData}
                  isFetching={isFetchingReviews}
                />
              )}
              <BusinessAddress {...componentProps} />
            </div>
            {/* Right column - Sticky sidebar */}
            <div
              className="flex-1 md:max-w-[336px] lg:sticky lg:top-[86px] lg:mt-0 lg:self-start"
              ref={ctaContainerRef}>
              <DealCTAContainer
                {...componentProps}
                onBookClick={!isMobileView ? onBookClick : undefined}
                onReviewsClick={onReviewsClick}
              />
            </div>
            {isMobileView && !loading && deal && (
              <DealSectionReviews
                {...componentProps}
                reviewsRef={reviewsRef}
                reviewsData={reviewsData}
                isFetching={isFetchingReviews}
              />
            )}
          </div>
        </div>

        {deal && (
          <FloatingCTAWrapper show={showStickyNav}>
            <ButtonAndPriceContainer
              price={deal.priceLabel}
              originalPrice={deal.originalPriceLabel}
              duration={deal.durationLabel}
              croLabelProps={croLabelProps}
              buttonProps={{ size: 'lg', label: _t('bookTime'), onClick: onBookClick }}
            />
          </FloatingCTAWrapper>
        )}
      </ContainerLayout>

      <ContainerLayout onlyAbove2xl>
        {loading && <DealsSectionPreviewSkeleton cards={6} />}
        {!loading && (
          <Fragment>
            {(searchPreview ?? []).map((section) => (
              <div key={section.id}>
                <DealsSectionPreview section={section} dispatch={dispatch} />
              </div>
            ))}
          </Fragment>
        )}
      </ContainerLayout>

      <BookAppointment />
      {deal && (
        <SEO
          title={_t('seo.dealDetails.title', {
            discount: deal.discountLabel ?? '',
            name: deal.name ?? '',
            merchant: deal.place.name,
          })}
          description={_t('seo.dealDetails.description', {
            name: deal.name ?? '',
            price: deal.priceLabel ?? '',
            originalPrice: deal.originalPriceLabel ?? '',
            merchant: deal.place.name,
          })}
          url={`${url.getBaseUrl()}${APP_ROUTES.DEAL_DETAILS}/${slugId}`}
          image={deal.imageUrl}
          noindex
        />
      )}
    </PageViewLayout>
  );
};

export default DealDetails;
